import React from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '~/UIKit';
import { ListItem } from '@mui/material';
import classNames from 'classnames';
import { PauseCircleOutline } from '@mui/icons-material';
import { IEvent } from '~/Models/DTO/Event.model';
import { useDateFns } from '~/Hooks';
import { useCalendarListItem } from './useCalendarListItem';
import { useStyles } from './CalendarListItem.style';

interface EventItemContainerProps {
  event: IEvent;
  breakTime?: number;
}

export const CalendarListItem = ({ event, breakTime = -1 }: EventItemContainerProps) => {
  const { t } = useTranslation();
  const { formatDate } = useDateFns();
  const { classes } = useStyles({
    color: event.color,
    hasBreak: breakTime > 0 && breakTime < 60,
    isCancelled: event.cancelled,
  })();
  const { location, goToDetails, eventDetails, isTimeVisible } = useCalendarListItem(event);

  return (
    <ListItem
      button
      className={classes.eventItemWrapper}
      data-testid={`EventItem-${event.id}`}
      onClick={goToDetails}
    >
      <div className={classes.eventItem}>
        <div className={classes.eventTimeWrapper}>
          {isTimeVisible && (
            <>
              <Typography className={classes.eventTimeText} component="span">
                {formatDate(event.start, 'HH:mm')}
              </Typography>
              <Typography
                className={classNames(classes.eventTimeText, classes.eventTimeTextLower)}
                component="span"
              >
                {formatDate(event.end, 'HH:mm')}
              </Typography>
            </>
          )}
        </div>
        <div className={classes.eventInfoWrapper}>
          <div className={classes.eventNameWrapper}>
            <Typography className={classes.eventNameText} component="span">
              {event.name}
            </Typography>
            <span className={classes.eventInfoTextWrapper}>
              {event.cancelled && (
                <Typography className={classes.eventInfoText}>
                  {t('app.event.cancelled')}
                </Typography>
              )}
              {!event.cancelled && !!event.locationId && !!location && (
                <Typography className={classes.eventInfoText} component="span">
                  {' '}
                  {eventDetails}
                </Typography>
              )}
            </span>
          </div>
        </div>
      </div>
      {isTimeVisible && breakTime > 0 && breakTime <= 120 && (
        <div className={classes.eventBreakTime} data-testid={`EventBreakTime-${event.id}`}>
          <PauseCircleOutline />
          {breakTime} {t('app.time.minutes')}
        </div>
      )}
      {isTimeVisible && breakTime > 120 && (
        <div className={classes.eventBreakTime} data-testid={`EventBreakTime-${event.id}`}>
          <PauseCircleOutline />
          {(breakTime / 60).toFixed(0)} {t('app.time.hours')}
        </div>
      )}
    </ListItem>
  );
};
